import { useI18n } from 'vue-i18n'
import {
  LOCALES,
  LocaleCode,
  type LocaleDefinition,
  getLocaleMeta,
  isLocaleCode,
  normalizeLocaleCodeValue,
} from '@packages/i18n'

type TranslationFn = (key: string, values?: unknown[] | Record<string, unknown>) => string

export type AppI18nApi = {
  /** 当前语言是普通字符串值，不是 Ref；调用方统一直接使用 locale，不要写 locale.value。 */
  locale: LocaleCode
  locales: LocaleDefinition[]
  availableLocales: LocaleDefinition[]
  localeMeta: LocaleDefinition
  localePath: any
  switchLocalePath: (targetLocale: LocaleCode) => string
  t: TranslationFn
}

const CHINESE_LOCALES = new Set<string>(['zh-cn', 'zh-tw', 'zh-hk', 'zh'])

/** 应用级默认语言，与 nuxt.config.ts 的 i18n.defaultLocale 保持同步。 */
const APP_DEFAULT_LOCALE: LocaleCode = 'en'

/** 当前站点暂不在语言切换菜单中展示的语言代码。 */
const HIDDEN_LOCALE_CODES = new Set<LocaleCode>([LocaleCode.It, LocaleCode.Ms])

/**
 * 统一判断当前语言是否属于中文系语言，避免各组件重复维护 locale 白名单。
 */
export const isChineseLocale = (locale: unknown) => {
  return CHINESE_LOCALES.has(String(locale).toLowerCase())
}

/**
 * 保留现有业务调用形态，并将实现委托给 @nuxtjs/i18n 提供的官方 composable。
 */
export const useAppI18n = (): AppI18nApi => {
  const { locale, localeProperties, t } = useI18n()
  const buildLocalePath = useLocalePath()
  const buildSwitchLocalePath = useSwitchLocalePath()
  const currentLocale = normalizeLocaleCodeValue(locale.value)
  const normalizedLocale = isLocaleCode(currentLocale) ? currentLocale : APP_DEFAULT_LOCALE
  const fallbackMeta = getLocaleMeta(normalizedLocale)
  const localeMeta: LocaleDefinition = {
    code: normalizedLocale,
    id: fallbackMeta.id,
    name: typeof localeProperties.value.name === 'string' ? localeProperties.value.name : fallbackMeta.name,
    language:
      typeof localeProperties.value.language === 'string' ? localeProperties.value.language : fallbackMeta.language,
    dir: localeProperties.value.dir === 'rtl' ? 'rtl' : 'ltr',
  }
  const localeDefinitions = LOCALES.filter((localeDefinition) => !HIDDEN_LOCALE_CODES.has(localeDefinition.code))

  /** 为当前语言或指定语言生成由 Nuxt I18n 管理的站内路径。 */
  const localePath = (path = '/', localeOverride?: LocaleCode) => {
    return buildLocalePath(path, localeOverride ?? normalizedLocale)
  }

  /** 为语言切换生成保留当前页面参数的目标路径。 */
  const switchLocalePath = (targetLocale: LocaleCode) => {
    return buildSwitchLocalePath(targetLocale)
  }

  return {
    locale: normalizedLocale,
    locales: localeDefinitions,
    availableLocales: localeDefinitions.filter((localeDefinition) => localeDefinition.code !== normalizedLocale),
    localeMeta,
    localePath,
    switchLocalePath,
    t: (key, values?) => String(t(key, values as any)),
  }
}
